import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Modal from "@material-ui/core/Modal";
import { ButtonStyled } from "components/buttons/buttons.styled";
import { AvatarChip } from "components/designV2/avatarChip/avatarChip";
import { CustomIcon } from "components/icon/customIcon.component";
import { Text } from "components/Typography/text.styled";
import { Color } from "config/colors";
import { FontFamily, FontSize } from "config/font";
import {
  ApplicationDto,
  ApplicationRequestDto,
  ApplicationResolution,
  ApplicationStatus,
} from "model/Application";
import { GigType } from "model/Gig";
import { useAlertStore } from "stores/alertStore/alertStore";
import { useApplicationStore } from "web-apps/company/stores/applicationStore/applicationStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useWorkerAvatar } from "web-apps/company/components/common/hooks/useWorkerAvatar";
import { ApplicantDetailsModal } from "web-apps/company/components/applicantDetails/applicantDetailsModal.component";
import { formatWorkerDateOfBirth } from "web-apps/company/utils/utils";
import {
  ApplicantRow,
  ApplicantIdentity,
  RowWrapper,
  WithdrawOfferModalBody,
} from "./gigApplicants.styled";

type Props = {
  readonly applicantData: ApplicationDto;
  readonly checkedApplications?: readonly ApplicationDto[];
  readonly setCheckedApplications?: React.Dispatch<
    React.SetStateAction<ApplicationDto[]>
  >;
  readonly handleShowContract?: (application: ApplicationDto) => void;
  readonly setApplications?: React.Dispatch<
    React.SetStateAction<ApplicationDto[]>
  >;
  readonly onWithdraw?: (applicationId: number) => void;
};

export const GigApplicantsListItem: React.FC<Props> = ({
  applicantData,
  checkedApplications,
  setCheckedApplications,
  handleShowContract,
  setApplications,
  onWithdraw,
}) => {
  const { t } = useTranslation();
  const [localChecked, setLocalChecked] = useState(false);
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [, , alertDispatch] = useAlertStore();
  const [, applicationActions] = useApplicationStore();
  const [companyState] = useCompanyStore();
  const [showApplicationDetails, setShowApplicationDetails] = useState(false);
  const history = useHistory();
  const isPublicShift = applicantData.gig.type === GigType.PUBLIC;
  const workerImageId =
    applicantData.worker.externalId?.trim() ||
    applicantData.worker.firebaseId?.trim() ||
    null;
  const applicantImg = useWorkerAvatar({
    workerExternalId: workerImageId,
    initialUrl:
      (workerImageId
        ? companyState.companyWorkerImages?.[workerImageId]
        : null) ?? null,
  });

  const isChecked =
    checkedApplications && setCheckedApplications
      ? checkedApplications.some(
          (application) => application.id === applicantData.id,
        )
      : localChecked;

  const toggleChecked = (): void => {
    const nextChecked = !isChecked;
    setLocalChecked(nextChecked);
    setCheckedApplications?.((currentApplications) =>
      nextChecked
        ? [...currentApplications, applicantData]
        : currentApplications.filter(
            (application) => application.id !== applicantData.id,
          ),
    );
  };

  const withdrawOffer = () => {
    const req: ApplicationRequestDto = {
      ...applicantData,
      status: ApplicationStatus.CLOSED,
      resolution: ApplicationResolution.WITHDRAWN,
    };

    if (companyState.company) {
      applicationActions
        .updateApplicationStatus(companyState.company.id, applicantData.id, req)
        .then(() => {
          setWithdrawModalOpen(false);
          alertDispatch({
            type: "SHOW_ALERT",
            payload: {
              icon: "checkmark",
              title: t("GigsCompany.Withdrawn"),
              message: t("GigsCompany.OfferWithdrawn"),
            },
          });

          if (onWithdraw) {
            onWithdraw(applicantData.id);
          }
        })
        .catch(() => {
          alertDispatch({
            type: "SHOW_ALERT",
            payload: {
              icon: "cross",
              title: t("Alerts.OhNo"),
              message: t("Alerts.SomethingWentWrong"),
            },
          });
        });
    }
  };

  const renderModalBody = () => {
    return (
      <>
        <Text
          align="center"
          fontSize={FontSize.H4}
          fontFamily={FontFamily.MontserratSemiBold}
        >
          {t("GigsCompany.WithdrawOffer")}
        </Text>

        <RowWrapper>
          <Text
            fontSize={FontSize.Large}
            color={Color.SeaBlue600}
            style={{ paddingTop: 25 }}
          >
            {t("GigsCompany.AreYouSureWithdrawOffer")}
          </Text>
        </RowWrapper>

        <RowWrapper>
          <ButtonStyled
            backgroundColor={Color.Disabled}
            color={Color.SeaBlue500}
            onClick={() => {
              setWithdrawModalOpen(false);
            }}
          >
            {t("General.Abort")}
          </ButtonStyled>
          <ButtonStyled
            onClick={() => {
              withdrawOffer();
            }}
          >
            {t("GigsCompany.CancelOffer")}
          </ButtonStyled>
        </RowWrapper>
      </>
    );
  };

  return (
    <>
      <ApplicantRow>
        <ApplicantIdentity
          onClick={() => {
            setShowApplicationDetails(true);
          }}
        >
          <AvatarChip
            title={`${applicantData.worker.firstName} ${applicantData.worker.lastName}`}
            subTitle={
              applicantData.worker.dateOfBirth?.trim()
                ? formatWorkerDateOfBirth(applicantData.worker.dateOfBirth)
                : ""
            }
            avatarUrl={applicantImg}
          />
        </ApplicantIdentity>
        {isPublicShift && (
          <Text color={Color.White} backgroundColor={Color.SeaBlue600} padding>
            {`${applicantData.requestedRate} ${t("General.Currency")}`}
          </Text>
        )}
        {handleShowContract && (
          <CustomIcon
            name="document"
            size="40px"
            color={Color.White}
            backgroundColor={Color.BurntSienna}
            padding="10px"
            onClick={() => {
              handleShowContract(applicantData);
            }}
            style={{ marginRight: 10 }}
          />
        )}
        {applicantData.status === ApplicationStatus.CLOSED &&
          (applicantData.resolution === ApplicationResolution.REJECTED ||
            applicantData.resolution === ApplicationResolution.WITHDRAWN ||
            applicantData.resolution === ApplicationResolution.DECLINED) && (
            <>
              <Text
                color={Color.White}
                backgroundColor={Color.SeaBlue600}
                padding
              >
                {t(`GigsCompany.${applicantData.resolution}`)}
              </Text>
            </>
          )}
        <CustomIcon
          name="chat"
          size="40px"
          color={Color.White}
          backgroundColor={Color.BurntSienna}
          padding="10px"
          onClick={() => {
            history.push(
              `/company/chat?userId=${applicantData.worker.id}&gigId=${applicantData.gigId}&applicationId=${applicantData.id}`,
            );
          }}
        />
        {applicantData.status === ApplicationStatus.PENDING && (
          <CustomIcon
            name="checkmark"
            size="27px"
            color={Color.White}
            backgroundColor={isChecked ? Color.BurntSienna : Color.White}
            padding="2px"
            onClick={() => {
              toggleChecked();
            }}
            style={{
              border: `2px solid ${Color.BurntSienna}`,
              flexShrink: 0,
              marginLeft: "15px",
            }}
          />
        )}
        {applicantData.status === ApplicationStatus.OFFERED && (
          <Text
            backgroundColor={Color.SeaBlue600}
            color={Color.White}
            fontSize={FontSize.Small}
            padding
          >
            {t("GigsCompany.Offered")}
          </Text>
        )}
        {applicantData.status === ApplicationStatus.OFFERED && (
          <CustomIcon
            onClick={() => setWithdrawModalOpen(true)}
            name="trashcan"
            size="48px"
            color={Color.Destructive}
            padding="10px"
          />
        )}

        <Modal
          open={withdrawModalOpen}
          onClose={() => {
            setWithdrawModalOpen(false);
          }}
        >
          <WithdrawOfferModalBody>{renderModalBody()}</WithdrawOfferModalBody>
        </Modal>
      </ApplicantRow>
      {showApplicationDetails && (
        <ApplicantDetailsModal
          applicant={applicantData}
          setShowApplicationModal={setShowApplicationDetails}
          showApplicationModal={showApplicationDetails}
          setApplications={setApplications}
        />
      )}
    </>
  );
};
